import { useCallback, useEffect, useState } from 'react';
import type { Operaciones } from '../../api/operaciones';
import type { OperacionesPortalRrpp } from '../../api/ops/portalRrpp';

/**
 * Las operaciones del portal. Viven en `api/ops/portalRrpp.ts` y se juntan en `crearOperaciones`; hasta que estén ahí,
 * este paso las toma del mismo objeto (en las pruebas se montan juntas).
 */
export type OpsPortal = Operaciones & OperacionesPortalRrpp;

export function opsDelPortal(ops: Operaciones): OpsPortal {
    return ops;
}

interface Estado<T> {
    base: string;
    clave: string;
    datos: T | null;
    error: unknown;
}

/**
 * Carga con su clave: cambia la clave, se vuelve a pedir. `recargar` repite la petición **sin vaciar** lo que ya se ve
 * (no parpadea); `poner` cambia los datos tras guardar sin volver a pedir. Sin clave (`null`), no pide nada.
 */
export function useCarga<T>(base: string | null, cargar: () => Promise<T>): { datos: T | null; error: unknown; cargando: boolean; recargar: () => void; poner: (d: T) => void } {
    const [intento, setIntento] = useState(0);
    const [estado, setEstado] = useState<Estado<T> | null>(null);
    const clave = base === null ? null : `${base}#${String(intento)}`;

    useEffect(() => {
        if (base === null || clave === null) {
            return;
        }
        let vivo = true;
        cargar()
            .then((datos) => {
                if (vivo) {
                    setEstado({ base, clave, datos, error: null });
                }
            })
            .catch((error: unknown) => {
                if (vivo) {
                    setEstado({ base, clave, datos: null, error });
                }
            });
        return () => {
            vivo = false;
        };
        // `cargar` cambia en cada pintado: manda la clave.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clave]);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    const poner = useCallback(
        (datos: T) => {
            if (base !== null && clave !== null) {
                setEstado({ base, clave, datos, error: null });
            }
        },
        [base, clave],
    );

    const mismaBase = estado !== null && estado.base === base;
    const actual = estado !== null && estado.clave === clave;
    return {
        datos: mismaBase ? estado.datos : null,
        error: actual ? estado.error : null,
        cargando: base !== null && !actual,
        recargar,
        poner,
    };
}

/** Copia al portapapeles; sin portapapeles (navegador antiguo o sin permiso), falla. */
export async function copiar(texto: string): Promise<void> {
    const portapapeles = navigator.clipboard as Clipboard | undefined;
    if (portapapeles === undefined) {
        throw new Error('Sin portapapeles');
    }
    await portapapeles.writeText(texto);
}

/** «sáb 17 oct · 21:30» en la zona del evento. */
export function fechaEvento(iso: string, zona?: string): string {
    const f = new Date(iso);
    if (Number.isNaN(f.getTime())) {
        return '';
    }
    const dia = f.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: zona });
    const hora = f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: zona });
    return `${dia} · ${hora}`;
}

/** «14 oct, 01:12». */
export function fechaCorta(iso: string | null | undefined): string {
    if (iso == null) {
        return '';
    }
    const f = new Date(iso);
    return Number.isNaN(f.getTime()) ? '' : `${f.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}, ${f.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
}
